import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectVideoCallCredentials } from '@brands/store/selectors/videoCallCredentials';
import { useEffect, useRef, useState } from 'react';
import Video, { ConnectOptions, LocalTrack, Room } from 'twilio-video';

import { Callback } from '../../services/communication/types/VideoRoom';
import { DataTrackManager } from '../../Utils/dataTrack';
import { useAppSelector } from '../useReduxHook';

interface IUseRoom {
  room: Video.Room | null;
  isConnecting: boolean;
  connect: (token: string, localTrack: LocalTrack[]) => Promise<void>;
}

const useRoom = (onError: Callback, options: ConnectOptions): IUseRoom => {
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const { is_visible: isVisible } = useAppSelector(selectVideoCallCredentials);
  const { userInfo } = useAppSelector(selectAuth);
  const optionsRef = useRef(options);
  const roomRef = useRef<Room | null>(null);
  const shouldDisconnectWhenModalHiddenRef = useRef(false);

  shouldDisconnectWhenModalHiddenRef.current = Boolean(userInfo) && userInfo?.role?.name !== UserRoleName.Patient;

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    roomRef.current = room;
  }, [room]);

  useEffect(() => {
    if (!room) return;

    if (room.state === 'disconnected') {
      return;
    }

    const shouldDisconnectWhenModalHidden = shouldDisconnectWhenModalHiddenRef.current;

    if (!shouldDisconnectWhenModalHidden || isVisible) {
      room.refreshInactiveMedia();
      return;
    }

    room.disconnect();
  }, [isVisible, room]);

  useEffect(() => {
    const disconnectRoom = (): void => {
      const currentRoom = roomRef.current;
      if (currentRoom && currentRoom.state !== 'disconnected') {
        currentRoom.disconnect();
      }
    };

    window.addEventListener('pagehide', disconnectRoom);
    window.addEventListener('beforeunload', disconnectRoom);

    return () => {
      window.removeEventListener('pagehide', disconnectRoom);
      window.removeEventListener('beforeunload', disconnectRoom);
      disconnectRoom();
    };
  }, []);

  const connect = async (token: string, localTrack: LocalTrack[]): Promise<void> => {
    setIsConnecting(true);

    DataTrackManager.reset();
    await Video.connect(token, { ...optionsRef.current, tracks: [...localTrack, DataTrackManager.dataTrack] }).then(
      (newRoom) => {
        roomRef.current = newRoom;
        setRoom(newRoom);

        newRoom.setMaxListeners(16);

        newRoom.localParticipant.videoTracks.forEach((publication) => publication.setPriority('low'));
        newRoom.localParticipant.on('trackPublished', (publication) => {
          if (publication.track === DataTrackManager.dataTrack) {
            if (DataTrackManager.dataTrackPublished.resolve) {
              DataTrackManager.dataTrackPublished.resolve();
            }
          }
        });
        newRoom.localParticipant.on('trackPublicationFailed', (error, track) => {
          if (track === DataTrackManager.dataTrack) {
            if (DataTrackManager.dataTrackPublished.reject) {
              DataTrackManager.dataTrackPublished.reject(error);
            }
          }
        });

        setIsConnecting(false);
      },
      (error) => {
        onError(error);
        setIsConnecting(false);
        // Already surfaced via onError above — mark it so callers up the chain don't toast again.
        (error as Error & { videoConnectFailureHandled?: boolean }).videoConnectFailureHandled = true;
        throw error;
      },
    );
  };

  return {
    room,
    isConnecting,
    connect,
  };
};

export default useRoom;
